import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import API from '../config/api';
import QuratorLayout from '../components/qurator/QuratorLayout';
import QureLayout from '../components/qure/QureLayout';

const HelpDesk = () => {
  const { user } = useAuth();
  const isQure = user?.user_type?.toLowerCase() === 'qure';
  const Layout = isQure ? QureLayout : QuratorLayout;
  const apiPrefix = isQure ? '/qure' : '/qurator';
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    // Load chat history from localStorage
    const savedHistory = localStorage.getItem('qurexr_chat_history');
    if (savedHistory) {
      try {
        const history = JSON.parse(savedHistory);
        setChatHistory(history);
        setMessages(history);
      } catch (error) {
        console.error('Error loading chat history:', error);
      }
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const autoResize = (textarea) => {
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 150) + 'px';
    }
  };

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.addEventListener('input', () => autoResize(textareaRef.current));
    }
  }, []);

  const formatMessage = (text) => {
    // Remove single asterisks, convert double asterisks to bold with orange color
    let formatted = text
      .replace(/\*\*(.*?)\*\*/g, '<strong style="color: #D95A17;">$1</strong>')
      .replace(/\*/g, '');
    
    // Split by line breaks and wrap in paragraphs
    const lines = formatted.split('\n').filter(line => line.trim());
    if (lines.length === 0) {
      return <p>{text}</p>;
    }
    return lines.map((line, index) => (
      <p key={index} dangerouslySetInnerHTML={{ __html: line }} style={{ textAlign: 'left', marginBottom: '0.5rem' }} />
    ));
  };

  const sendMessage = async (messageText = null) => {
    const messageToSend = messageText || inputMessage.trim();
    if (isProcessing || !messageToSend) return;
    
    setInputMessage('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    
    // Add user message
    const newUserMessage = {
      type: 'user',
      message: messageToSend,
      timestamp: new Date().toLocaleTimeString('en-US', { 
        hour12: true, 
        hour: '2-digit', 
        minute: '2-digit'
      })
    };
    
    const updatedMessages = [...messages, newUserMessage];
    setMessages(updatedMessages);
    setIsProcessing(true);
    
    try {
      const response = await API.post(`${apiPrefix}/help-desk/chat`, {
        message: messageToSend,
        chat_history: chatHistory
      });
      
      if (response.data && (response.data.ok === true || response.data.success === true)) {
        const aiMessage = {
          type: 'assistant',
          message: response.data.response || response.data.message || 'No response received.',
          timestamp: new Date().toLocaleTimeString('en-US', { 
            hour12: true, 
            hour: '2-digit', 
            minute: '2-digit'
          })
        };
        
        const finalMessages = [...updatedMessages, aiMessage];
        setMessages(finalMessages);
        setChatHistory(finalMessages);
        localStorage.setItem('qurexr_chat_history', JSON.stringify(finalMessages));
      } else {
        throw new Error(response.data?.error || response.data?.message || 'Failed to get response');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorDetails = error.response?.data?.error || error.response?.data?.message || error.message || 'Unknown error';
      const errorMessage = {
        type: 'assistant',
        message: `Sorry, I encountered an error: ${errorDetails}. Please try again.`,
        timestamp: new Date().toLocaleTimeString('en-US', { 
          hour12: true, 
          hour: '2-digit', 
          minute: '2-digit'
        })
      };
      setMessages([...updatedMessages, errorMessage]);
    } finally {
      setIsProcessing(false);
    }
  };

  const clearChat = async () => {
    if (!window.confirm('Are you sure you want to clear the chat history?')) {
      return;
    }
    
    try {
      await API.post(`${apiPrefix}/help-desk/clear-chat`);
      setMessages([]);
      setChatHistory([]);
      localStorage.removeItem('qurexr_chat_history');
    } catch (error) {
      console.error('Error clearing chat:', error);
      // Clear locally anyway
      setMessages([]);
      setChatHistory([]);
      localStorage.removeItem('qurexr_chat_history');
    }
  };

  const sendSuggestion = (suggestion) => {
    // Directly process without setting input
    sendMessage(suggestion);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const suggestions = [
    'How do I create a new AR simulation?',
    'What is the experimental design workflow?',
    'How do I publish my simulation?',
    'How do I schedule experiments?',
    'What devices are supported?',
    'How do I manage teams?',
    'How do I use the AI assistant?',
    'What is Mixed Reality?',
    'How do I upload reference materials?',
    'How do I view analytics?'
  ];

  return (
    <Layout>
      <div className={`help-desk-container ${isQure ? 'qure-theme' : 'qurator-theme'}`}>
        <div className="chat-card">
          {/* Chat Header */}
          <div className="chat-header">
            <div className="chat-header-left">
              <i className="bi bi-robot chat-header-icon"></i>
              <div className="chat-header-title">
                <h2>AI Assistant</h2>
                <p className="chat-header-subtitle">Your intelligent companion for QureXR guidance</p>
              </div>
            </div>
            <button className="clear-chat-btn" onClick={clearChat}>
              <i className="bi bi-trash"></i>
              <span>Clear Chat</span>
            </button>
          </div>

          {/* Chat Messages */}
          <div className="chat-messages" id="chatMessages">
            {messages.length === 0 ? (
              <div className="welcome-message">
                <i className="bi bi-robot"></i>
                <h3>Welcome to QureXR AI Assistant!</h3>
                <p>Ask me anything about AR/MR simulations, experiments, or the QureXR platform.</p>
                
                <div className="suggestions">
                  {suggestions.map((suggestion, index) => (
                    <div 
                      key={index}
                      className="suggestion-chip" 
                      onClick={() => sendSuggestion(suggestion)}
                    >
                      {suggestion}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((chat, index) => (
                <div key={index} className={`message ${chat.type}`}>
                  <div className="message-avatar">
                    {chat.type === 'user' ? (
                      <i className="bi bi-person"></i>
                    ) : (
                      <i className="bi bi-robot"></i>
                    )}
                  </div>
                  <div className="message-content">
                    <div className="message-text">
                      {chat.type === 'assistant' ? formatMessage(chat.message) : chat.message.split('\n').map((line, i) => (
                        <React.Fragment key={i}>
                          {line}
                          {i < chat.message.split('\n').length - 1 && <br />}
                        </React.Fragment>
                      ))}
                    </div>
                    <div className="message-time">
                      {chat.timestamp}
                    </div>
                  </div>
                </div>
              ))
            )}

            {/* Typing Indicator */}
            {isProcessing && (
              <div className="typing-indicator">
                <div className="message-avatar">
                  <i className="bi bi-robot"></i>
                </div>
                <div className="typing-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Chat Input */}
          <div className="chat-input-area">
            <form className="chat-input-wrapper" onSubmit={(e) => { e.preventDefault(); sendMessage(); }}>
              <textarea 
                ref={textareaRef}
                className="chat-input" 
                id="messageInput" 
                placeholder="Type your message here..."
                rows="1"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isProcessing}
              />
              <button 
                type="submit" 
                className="send-button" 
                id="sendButton"
                disabled={isProcessing || !inputMessage.trim()}
              >
                <i className="bi bi-send-fill"></i>
              </button>
            </form>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default HelpDesk;
